import { useLanguage } from "@/contexts/LanguageContext"

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const { t } = useLanguage()

  const footerLinks = [
    { href: "#services", label: t('footer.services') },
    { href: "#process", label: t('footer.process') },
    { href: "#projects", label: t('footer.projects') },
    { href: "#contact", label: t('footer.contact') }
  ]

  return (
    <footer className="bg-je-black text-je-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          {/* Logo et description */}
          <div className="mb-8 md:mb-0">
            <div className="text-2xl font-black text-je-red mb-2">
              JE HEPIA
            </div>
            <p className="text-gray-400 max-w-md mb-2">
              {t('footer.description')}
            </p>
            <p className="text-gray-400 text-sm">
              Rue de la Prairie 4, 1202 Genève
            </p>
          </div>

          {/* Navigation links et réseaux sociaux */}
          <div className="flex flex-col items-center space-y-6">
            <div className="flex flex-wrap justify-center gap-6">
              {footerLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-gray-400 hover:text-je-white transition-colors duration-200"
                >
                  {link.label}
                </a>
              ))}
            </div>
            
            {/* Réseaux sociaux */}
            <div className="flex space-x-4">
              <a
                href="https://www.instagram.com/jehepia/?hl=fr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-je-red rounded-full flex items-center justify-center text-je-white hover:bg-primary-hover transition-colors duration-200"
                aria-label="Suivez-nous sur Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/jehepia/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-je-red rounded-full flex items-center justify-center text-je-white hover:bg-primary-hover transition-colors duration-200"
                aria-label="Suivez-nous sur LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} {t('footer.copyright')}
            </p>
            
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-je-white transition-colors">
                {t('footer.legal')}
              </a>
              <a href="#" className="text-gray-400 hover:text-je-white transition-colors">
                {t('footer.privacy')}
              </a>
              <a href="#" className="text-gray-400 hover:text-je-white transition-colors">
                {t('footer.terms')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer